import { useEffect, useRef, useState, type RefObject } from 'react'
import { Box, Divider, Grid, HStack, Text, VStack } from '@chakra-ui/react'
import { CheckCircle2, Circle } from 'lucide-react'
import { apiClient } from '@/lib/api/client'
import { getApiErrorMessage } from '@/lib/api/errorMessages'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { ClientPurchaseOrder, ParentTicketFormValues, RequestOption } from '@/types/ticket'
import type { FEDispatchSelection } from '@/components/forms/parentTicket/FEDispatchFlow'
import { additionalFeOrdinalLabel } from '@/components/forms/parentTicket/feDispatchRole'
import { formatDispatchWhen } from '@/components/forms/parentTicket/feDispatchUtils'
import { isDispatchChecklistComplete } from '@/lib/utils/parentTicketSubmitValidation'

export type TicketDetailsPreview = {
  severity?: string | null
  serviceType?: string | null
  clientReferenceNumber?: string | null
}

export type LocationPreview = {
  region?: string | null
  areaCluster?: string | null
  province?: string | null
  town?: string | null
}

type Props = {
  form: ParentTicketFormValues
  isEditMode: boolean
  selectedRequest: RequestOption | null
  agentLabel: string
  assignToMe: boolean
  requiresDispatch: boolean
  dispatchReadiness?: 'pending' | 'on_hold' | 'ready'
  sitePinConfirmed?: boolean
  dispatchSelection: FEDispatchSelection | null
  dispatchAdditionalSelections?: FEDispatchSelection[]
  purchaseOrder: ClientPurchaseOrder | null
  clientName?: string
  formatPoDate: (value?: string | null) => string
  ticketDetails: TicketDetailsPreview
  location: LocationPreview
  scrollContainerRef?: RefObject<HTMLElement | null>
}

function RailSection({ title, children }: { title: string; children: React.ReactNode }) {
  const colors = useThemeColors()
  return (
    <Box>
      <Text fontSize="11px" fontWeight="600" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.04em" mb={2}>
        {title}
      </Text>
      {children}
    </Box>
  )
}

function PreviewField({
  label,
  value,
  placeholder = '—',
}: {
  label: string
  value?: string | null
  placeholder?: string
}) {
  const colors = useThemeColors()
  const display = value?.trim() || placeholder
  const isEmpty = !value?.trim()

  return (
    <Box>
      <Text fontSize="11px" color={colors.textMuted} mb={0.5}>
        {label}
      </Text>
      <Text
        fontSize="12px"
        color={isEmpty ? colors.textMuted : colors.text}
        fontWeight={isEmpty ? '400' : '500'}
      >
        {display}
      </Text>
    </Box>
  )
}

function ChecklistRow({
  done,
  label,
  detail,
}: {
  done: boolean
  label: string
  detail?: string | null
}) {
  const colors = useThemeColors()
  return (
    <HStack align="flex-start" spacing={2}>
      <Box mt="2px" flexShrink={0} color={done ? 'green.500' : colors.textMuted}>
        {done ? <CheckCircle2 size={14} /> : <Circle size={14} />}
      </Box>
      <Box minW={0}>
        <Text fontSize="12px" fontWeight={done ? '500' : '400'} color={done ? colors.text : colors.textSecondary}>
          {label}
        </Text>
        {detail ? (
          <Text fontSize="11px" color={colors.textMuted} mt="1px" whiteSpace="pre-wrap">
            {detail}
          </Text>
        ) : null}
      </Box>
    </HStack>
  )
}

function requestSummaryLine(request: RequestOption): string {
  const parts: string[] = []
  const orgOrClient = request.clientLabel || request.clientOrg || request.clientName
  if (orgOrClient) parts.push(orgOrClient)
  if (request.contactName) parts.push(request.contactName)
  if (request.contactNumber) parts.push(request.contactNumber)
  if (request.siteAddress) parts.push(request.siteAddress)
  if (request.serialNumber) parts.push(`SN: ${request.serialNumber}`)
  return parts.join(' · ')
}

function normalizeCategoryRows(data: unknown): Array<{ id: number; name: string }> {
  const rows = Array.isArray(data) ? data : (data as { data?: unknown[] })?.data ?? []
  return (rows as Array<Record<string, unknown>>).map((row) => ({
    id: Number(row.id),
    name: String(row.label ?? row.name ?? ''),
  }))
}

function useIssueClassificationSummary(form: ParentTicketFormValues): {
  summary: string | null
  errorMessage: string | null
} {
  const [summary, setSummary] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      if (!form.issueCategoryId) {
        setSummary(null)
        setErrorMessage(null)
        return
      }

      try {
        const labels: string[] = []
        const rootsRes = await apiClient.get('/options/issue-categories')
        const roots = normalizeCategoryRows(rootsRes.data)
        const root = roots.find((row) => row.id === form.issueCategoryId)
        if (root?.name) labels.push(root.name)

        if (form.issueSubCategoryId) {
          const subsRes = await apiClient.get(`/options/issue-categories/${form.issueCategoryId}/children`)
          const subs = normalizeCategoryRows(subsRes.data)
          const sub = subs.find((row) => row.id === form.issueSubCategoryId)
          if (sub?.name) labels.push(sub.name)
        }

        if (form.issueSubCategoryId && form.issueItemId) {
          const itemsRes = await apiClient.get(`/options/issue-categories/${form.issueSubCategoryId}/children`)
          const items = normalizeCategoryRows(itemsRes.data)
          const item = items.find((row) => row.id === form.issueItemId)
          if (item?.name) labels.push(item.name)
        }

        if (!cancelled) {
          setSummary(labels.length > 0 ? labels.join(' › ') : null)
          setErrorMessage(null)
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setSummary(null)
          setErrorMessage(
            getApiErrorMessage(error, 'Issue classification lookup is temporarily unavailable.'),
          )
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [form.issueCategoryId, form.issueSubCategoryId, form.issueItemId])

  return { summary, errorMessage }
}

export function ParentTicketFormContextRail({
  form,
  isEditMode,
  selectedRequest,
  agentLabel,
  assignToMe,
  requiresDispatch,
  dispatchReadiness = 'pending',
  sitePinConfirmed = false,
  dispatchSelection,
  dispatchAdditionalSelections,
  purchaseOrder,
  clientName,
  formatPoDate,
  ticketDetails,
  location,
  scrollContainerRef,
}: Props) {
  const colors = useThemeColors()
  const linkedRequestRef = useRef<HTMLDivElement>(null)
  const issueClassification = useIssueClassificationSummary(form)

  const hasRequest = Boolean(form.requestId?.trim())
  const hasSubject = Boolean(form.subject?.trim())
  const hasSeverity = Boolean((form.severity ?? form.priority ?? '').trim())
  const hasDescription = Boolean(form.issueDescription?.trim())
  const hasAgent = Boolean(form.agentId?.trim())
  const dispatchReady = isDispatchChecklistComplete({
    requiresDispatch,
    dispatchReadiness,
    hasDispatchSelection: Boolean(dispatchSelection),
    sitePinConfirmed,
    latitude: dispatchSelection?.latitude ?? form.incidentLocationLatitude,
    longitude: dispatchSelection?.longitude ?? form.incidentLocationLongitude,
  })
  const hasLocation = [location.region, location.areaCluster, location.province, location.town].some(Boolean)
  const hasNotes = Boolean(form.notes?.trim())

  const requiredDone = [hasRequest, hasSubject, hasSeverity, hasDescription, hasAgent, dispatchReady]
  const requiredCount = requiredDone.filter(Boolean).length
  const requiredTotal = requiredDone.length
  const readyToSubmit = requiredCount === requiredTotal

  const agentDisplay = assignToMe
    ? agentLabel
    : hasAgent
      ? agentLabel || `User #${form.agentId}`
      : null

  const billableLabel =
    form.billable === true ? 'Billable' : form.billable === false ? 'Non-billable' : null

  const severityDisplay =
    ticketDetails.severity ??
    (hasSeverity ? `Severity ${form.severity ?? form.priority}` : null)

  const backupSelections = dispatchAdditionalSelections ?? []
  const dispatchChecklistDetail =
    dispatchReadiness === 'pending'
      ? 'Pending — FE not required yet'
      : dispatchReadiness === 'on_hold'
        ? 'On hold — FE not required yet'
        : [
            sitePinConfirmed ? 'Pin confirmed' : 'Pin not confirmed',
            dispatchSelection?.feName,
            ...backupSelections.map((backup) => backup.feName),
          ]
            .filter(Boolean)
            .join(' · ') || null

  useEffect(() => {
    if (!selectedRequest || !scrollContainerRef?.current) return
    const timer = window.setTimeout(() => {
      const container = scrollContainerRef.current
      const target = linkedRequestRef.current
      if (!container || !target) return
      const containerRect = container.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      if (targetRect.top < containerRect.top || targetRect.bottom > containerRect.bottom) {
        const scrollTop = target.offsetTop - container.offsetTop - 8
        container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' })
      }
    }, 80)
    return () => window.clearTimeout(timer)
  }, [selectedRequest?.id, scrollContainerRef])

  return (
    <Box
      w="100%"
      borderWidth="0.5px"
      borderColor={colors.borderLight ?? colors.border}
      borderRadius="lg"
      bg={colors.cardBg}
      p="14px 16px"
      fontSize="13px"
    >
      <VStack align="stretch" spacing={4} divider={<Divider borderColor={colors.borderLight} />}>
        <RailSection title="Ticket">
          <Text fontSize="sm" fontWeight="600" color={colors.text} fontFamily="mono">
            {form.ticketId?.trim() || '—'}
          </Text>
          <Text fontSize="11px" color={colors.textMuted} mt={1}>
            {isEditMode ? 'Editing existing ticket' : 'Draft ID — assigned on save'}
          </Text>
          {hasSubject ? (
            <Text fontSize="12px" color={colors.text} mt={2} fontWeight="500" whiteSpace="pre-wrap">
              {form.subject}
            </Text>
          ) : (
            <Text fontSize="12px" color={colors.textMuted} mt={2}>
              No subject yet
            </Text>
          )}
        </RailSection>

        <RailSection title="Linked request">
          <Box ref={linkedRequestRef}>
            {selectedRequest ? (
              <>
                <Text fontSize="sm" fontWeight="600" color={colors.text}>
                  {selectedRequest.id}
                </Text>
                <Text fontSize="12px" color={colors.textSecondary} mt={1}>
                  {requestSummaryLine(selectedRequest) || selectedRequest.clientName}
                </Text>
                {selectedRequest.issueDescription ? (
                  <Text fontSize="11px" color={colors.textMuted} mt={2}>
                    {selectedRequest.issueDescription}
                  </Text>
                ) : null}
                {selectedRequest.preferredScheduleAt ? (
                  <Text fontSize="11px" color={colors.textMuted} mt={1}>
                    Preferred schedule: {selectedRequest.preferredScheduleAt}
                    {selectedRequest.preferredScheduleUntil ? ` to ${selectedRequest.preferredScheduleUntil}` : ''}
                  </Text>
                ) : selectedRequest.contactAvailabilityText ? (
                  <Text fontSize="11px" color={colors.textMuted} mt={1}>
                    Availability: {selectedRequest.contactAvailabilityText}
                  </Text>
                ) : null}
                {selectedRequest.clientLabel ? (
                  <Text fontSize="11px" color={colors.textMuted} mt={1}>
                    Company: {selectedRequest.clientLabel}
                  </Text>
                ) : null}
                {selectedRequest.siteAddress ? (
                  <Text fontSize="11px" color={colors.textMuted} mt={1}>
                    Address: {selectedRequest.siteAddress}
                  </Text>
                ) : null}
                {selectedRequest.contactAvailabilityText && selectedRequest.preferredScheduleAt ? (
                  <Text fontSize="11px" color={colors.textMuted} mt={1}>
                    Availability: {selectedRequest.contactAvailabilityText}
                  </Text>
                ) : null}
                {selectedRequest.contactNumber ? (
                  <Text fontSize="11px" color={colors.textMuted} mt={1}>
                    Contact: {selectedRequest.contactName || '—'} · {selectedRequest.contactNumber}
                  </Text>
                ) : null}
                {selectedRequest.priority || selectedRequest.ticketStatusLabel ? (
                  <Text fontSize="11px" color={colors.textMuted} mt={1}>
                    {[selectedRequest.priority, selectedRequest.ticketStatusLabel].filter(Boolean).join(' · ')}
                  </Text>
                ) : null}
              </>
            ) : (
              <Text fontSize="12px" color={colors.textMuted}>No request linked yet</Text>
            )}
          </Box>
        </RailSection>

        <RailSection title="Ticket details">
          <VStack align="stretch" spacing={2}>
            <PreviewField label="Severity" value={severityDisplay} />
            <PreviewField label="Service" value={ticketDetails.serviceType} />
            <PreviewField label="Client reference" value={ticketDetails.clientReferenceNumber} />
            <Box>
              <Text fontSize="11px" color={colors.textMuted} mb={0.5}>
                Description
              </Text>
              {hasDescription ? (
                <Text fontSize="12px" color={colors.text} whiteSpace="pre-wrap" noOfLines={4}>
                  {form.issueDescription}
                </Text>
              ) : (
                <Text fontSize="12px" color={colors.textMuted}>
                  —
                </Text>
              )}
            </Box>
          </VStack>
        </RailSection>

        <RailSection title="Issue classification">
          {issueClassification.summary ? (
            <Text fontSize="12px" color={colors.text} fontWeight="500">
              {issueClassification.summary}
            </Text>
          ) : issueClassification.errorMessage ? (
            <Text fontSize="12px" color="orange.600">
              {issueClassification.errorMessage}
            </Text>
          ) : (
            <Text fontSize="12px" color={colors.textMuted}>Not classified yet</Text>
          )}
        </RailSection>

        <RailSection title="Location">
          {hasLocation ? (
            <VStack align="stretch" spacing={2}>
              <PreviewField label="Region" value={location.region} />
              <PreviewField label="Area cluster" value={location.areaCluster} />
              <PreviewField label="Province" value={location.province} />
              <PreviewField label="Town" value={location.town} />
            </VStack>
          ) : (
            <Text fontSize="12px" color={colors.textMuted}>No location selected</Text>
          )}
        </RailSection>

        <RailSection title="Client & billing">
          <Box mb={3}><PreviewField label="Client" value={clientName} placeholder="Client not assigned" /></Box>
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <Box>
              <Text fontSize="11px" color={colors.textMuted} mb={1}>Purchase order</Text>
              {purchaseOrder ? (
                <>
                  <Text fontSize="sm" fontWeight="500" color={colors.text}>
                    {purchaseOrder.po_number}
                  </Text>
                  {purchaseOrder.po_date ? (
                    <Text fontSize="11px" color={colors.textMuted} mt={1}>
                      {formatPoDate(purchaseOrder.po_date)}
                    </Text>
                  ) : null}
                </>
              ) : (
                <Text fontSize="12px" color={colors.textMuted}>No PO selected</Text>
              )}
            </Box>
            <Box>
              <Text fontSize="11px" color={colors.textMuted} mb={1}>Billing</Text>
              {billableLabel ? (
                <Text fontSize="sm" fontWeight="500" color={colors.text}>
                  {billableLabel}
                </Text>
              ) : (
                <Text fontSize="12px" color={colors.textMuted}>Billing not determined</Text>
              )}
            </Box>
          </Grid>
        </RailSection>

        {requiresDispatch && (
          <RailSection title="Field dispatch">
            {dispatchSelection || backupSelections.length > 0 ? (
              <VStack align="stretch" spacing={2}>
                {dispatchSelection ? (
                  <Box>
                    <Text fontSize="11px" color={colors.textMuted} fontWeight="600">Primary</Text>
                    <Text fontSize="sm" fontWeight="500" color={colors.text}>
                      {dispatchSelection.feName}
                    </Text>
                    <Text fontSize="11px" color={colors.textSecondary} mt={1}>
                      {formatDispatchWhen(dispatchSelection)}
                    </Text>
                    <Text fontSize="11px" color={colors.textMuted} mt={1}>
                      {dispatchSelection.storeName}
                    </Text>
                  </Box>
                ) : null}
                {backupSelections.map((backup, index) => (
                  <Box key={`${backup.feUserId}-${index}`} pt={dispatchSelection || index > 0 ? 1 : 0}>
                    <Text fontSize="11px" color={colors.textMuted} fontWeight="600">
                      {backupSelections.length > 1
                        ? additionalFeOrdinalLabel(index)
                        : 'Additional FE'}
                    </Text>
                    <Text fontSize="sm" fontWeight="500" color={colors.text}>
                      {backup.feName}
                    </Text>
                    <Text fontSize="11px" color={colors.textSecondary} mt={1}>
                      {formatDispatchWhen(backup)}
                    </Text>
                    <Text fontSize="11px" color={colors.textMuted} mt={1}>
                      {backup.storeName}
                    </Text>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text fontSize="12px" color="orange.600">Dispatch started — confirm a slot in the form</Text>
            )}
          </RailSection>
        )}

        <RailSection title="Agent">
          {agentDisplay ? (
            <Text fontSize="12px" color={colors.text} fontWeight="500">
              {agentDisplay}
            </Text>
          ) : (
            <Text fontSize="12px" color={colors.textMuted}>Not assigned</Text>
          )}
        </RailSection>

        {hasNotes ? (
          <RailSection title="Notes">
            <Text fontSize="12px" color={colors.text} whiteSpace="pre-wrap">
              {form.notes}
            </Text>
          </RailSection>
        ) : null}

        <RailSection title="Submit checklist">
          <VStack align="stretch" spacing={2}>
            <ChecklistRow done={hasRequest} label="Request linked" detail={selectedRequest?.id} />
            <ChecklistRow done={hasSubject} label="Subject" detail={form.subject} />
            <ChecklistRow done={hasSeverity} label="Severity" detail={severityDisplay} />
            <ChecklistRow done={hasDescription} label="Description" detail={hasDescription ? 'Filled' : null} />
            <ChecklistRow done={hasAgent} label="Agent assigned" detail={agentDisplay} />
            {requiresDispatch ? (
              <ChecklistRow
                done={dispatchReady}
                label={
                  dispatchReadiness === 'ready'
                    ? 'FE dispatch ready (pin + primary FE)'
                    : 'FE dispatch (pending/hold OK)'
                }
                detail={dispatchChecklistDetail}
              />
            ) : null}
          </VStack>
          <Box
            mt={3}
            pt={3}
            borderTop="0.5px solid"
            borderColor={colors.borderLight}
          >
            <Text fontSize="12px" fontWeight="600" color={readyToSubmit ? 'green.600' : colors.textSecondary}>
              {readyToSubmit
                ? 'Ready to submit'
                : `${requiredCount} of ${requiredTotal} required items complete`}
            </Text>
          </Box>
        </RailSection>
      </VStack>
    </Box>
  )
}
